import { faCalendarClock, faGear, faList, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Meta } from 'src/components/Meta';
import { Stack } from 'src/components/Stack';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { AdaptiveSideMenu } from 'src/shared/AdaptiveSideMenu/AdaptiveSideMenu';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';

import FranchiseFeePayerFormPage from '../HQ/FranchiseFeePayers/FranchiseFeePayerFormPage';
import FranchiseFeePayersPage from '../HQ/FranchiseFeePayers/FranchiseFeePayersPage';

import ActivityPage from './ActivityPage';
import FranchiseFeeFormPage from './FeeFormPage';
import FranchiseFeesPage from './FeesPage';
import { PayoutDetailPage, PayoutPage } from './Payouts';

export default function FranchiseLayout() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.page',
  });
  const { session } = useSession();

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { tabName, id } = useParams();

  const activeTab = tabName || 'fees';
  const isFormPage = activeTab === 'new' || (id && activeTab === 'edit');
  const isPayoutDetailPage = activeTab === 'payouts' && id;
  const isPayerFormPage = activeTab === 'payers' && id;

  const tabs = [
    {
      icon: faGear,
      label: t('IndexTitle'),
      value: 'fees',
      component: <FranchiseFeesPage />,
      show: true,
    },
    {
      icon: faCalendarClock,
      label: t('ActivityTitle'),
      value: 'activity',
      component: <ActivityPage />,
      show: true,
    },
    {
      icon: faList,
      label: t('PayoutsTitle'),
      value: 'payouts',
      component: <PayoutPage />,
      show: true,
    },
    {
      icon: faUsers,
      label: t('PayersTitle'),
      value: 'payers',
      component: <FranchiseFeePayersPage />,
      show: true,
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.value === activeTab
  )?.component;

  // Handle form pages
  if (isFormPage) {
    return <FranchiseFeeFormPage />;
  }

  // Handle payout detail page
  if (isPayoutDetailPage) {
    return <PayoutDetailPage />;
  }

  // Handle payer form pages (create/edit)
  if (isPayerFormPage) {
    return <FranchiseFeePayerFormPage />;
  }

  return (
    <>
      <Meta title={t('title')} brand={session?.brand} />
      <AppFrameConfig
        id="franchise-fees"
        containerMaxWidth={'xl'}
        title={t('title')}
      />
      <Stack direction={mdDown ? 'column' : 'row'}>
        <AdaptiveSideMenu
          navItems={tabs}
          activeValue={activeTab}
          onValueChange={(value) => navigate(`/franchise-fees/${value}`)}
          collapsible={true}
          title={t('franchiseFeesSections')}
          id="franchise-fees"
        />
        <Box sx={{ pl: 2, width: '100%', transition: 'all 0.3s ease' }}>
          {activeTabContent}
        </Box>
      </Stack>
    </>
  );
}
